import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@playbook/utils';

import css from './styles.module.css';

export type BadgeVariant =
	| 'neutral'
	| 'information'
	| 'warning'
	| 'error'
	| 'success';

const DefaultBadgeVariant: BadgeVariant = 'neutral' as const;

/**
 *	How much of the variant's colour the chip carries. `100` through `300` tint a
 *	pale surface progressively deeper; `filled` is the saturated fill.
 */
export type BadgeTint = '100' | '200' | '300' | 'filled';

const DefaultBadgeTint: BadgeTint = '100' as const;

type BadgeEventHandlers = {
	[K in keyof ComponentProps<'span'> as K extends `on${string}`
		? K
		: never]?: ComponentProps<'span'>[K];
};

interface BadgeDataAttributes {
	[key: `data-${string}`]: string | number | boolean | undefined;
}

interface BadgeProperties extends BadgeEventHandlers, BadgeDataAttributes {
	variant?: BadgeVariant;
	tint?: BadgeTint;
	dot?: boolean;

	label: string;

	leadingIcon?: ReactNode;
	trailingIcon?: ReactNode;
}

export function Badge({
	variant = DefaultBadgeVariant,
	tint = DefaultBadgeTint,
	dot = false,

	label,

	leadingIcon,
	trailingIcon,

	...props
}: BadgeProperties): ReactNode {
	/* A class cannot begin with a digit, so the tint is prefixed. */
	const className: string = cn(
		css.badge,
		css[variant],
		css[`tint-${tint}`],
		dot && css.dotted
	);

	return (
		<span
			{...props}
			data-variant={variant}
			data-tint={tint}
			data-dot={dot}
			className={className}>
			{dot ? <span className={css.dot} /> : null}
			{leadingIcon}
			<span className={css.text}>{label}</span>
			{trailingIcon}
		</span>
	);
}
